import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MenuItem, MenuCategory } from "@/types/menu";
import { supabase } from "@/integrations/supabase/client";

interface EdgeFunctionResponse {
  data: MenuItem[] | null;
  error: string | null;
}

interface MenuItemFilters {
  categoryId?: string;
  searchTerm?: string;
  availableOnly?: boolean;
}

interface MenuItemViewRow {
  menu_item_id: string | number;
  item_name: string;
  item_description: string | null;
  price: number;
  available: boolean;
  image_url: string | null;
  store_id: string;
  created_at: string;
  updated_at: string;
  category_id: string;
  category_name: string;
  category_description: string | null;
  category_position: number;
  tags: string[] | null;
}

// Enhanced cache configuration
const CACHE_CONFIG = {
  // Menu items cache for 1 hour
  MENU_ITEMS_STALE_TIME: 60 * 60 * 1000, // 1 hour
  MENU_ITEMS_GC_TIME: 2 * 60 * 60 * 1000, // 2 hours
  
  // Categories cache for 2 hours (less frequently changed)
  CATEGORIES_STALE_TIME: 2 * 60 * 60 * 1000, // 2 hours
  CATEGORIES_GC_TIME: 4 * 60 * 60 * 1000, // 4 hours
  
  // Retry configuration
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
};

// New function to query view directly using Supabase SDK with enhanced caching
const fetchMenuItemsFromView = async (storeId: string, categoryId?: string): Promise<MenuItem[]> => {
  try {
    let query;

    if (categoryId && categoryId !== 'all') {
      // Use the view to get items for specific category and store
      query = supabase
        .from('menu_items_by_category')
        .select('*')
        .eq('store_id', storeId)
        .eq('category_id', categoryId)
        .order('category_position', { ascending: true })
        .order('item_name', { ascending: true });
    } else {
      // Get all menu items for the store using the view
      query = supabase
        .from('menu_items_by_category')
        .select('*')
        .eq('store_id', storeId)
        .order('category_position', { ascending: true })
        .order('item_name', { ascending: true });
    }

    const { data: queryResults, error: queryError } = await query;

    if (queryError) {
      console.error('Error fetching menu items from view:', queryError);
      throw new Error(queryError.message);
    }

    if (!queryResults || queryResults.length === 0) {
      return [];
    }

    // Transform view results to MenuItem format
    const itemsMap = new Map<string, MenuItem>();
    
    queryResults.forEach((row: MenuItemViewRow) => {
      const itemId = row.menu_item_id.toString();
      
      if (!itemsMap.has(itemId)) {
        itemsMap.set(itemId, {
          id: itemId,
          name: row.item_name,
          description: row.item_description,
          price: row.price,
          available: row.available,
          image_url: row.image_url,
          store_id: row.store_id,
          created_at: row.created_at,
          updated_at: row.updated_at,
          categoryIds: [row.category_id],
          categories: [{
            id: row.category_id,
            name: row.category_name,
            description: row.category_description
          }],
          tags: row.tags || []
        });
      } else {
        // Add additional categories if item exists in multiple categories
        const existingItem = itemsMap.get(itemId)!;
        if (!existingItem.categoryIds.includes(row.category_id)) {
          existingItem.categoryIds.push(row.category_id);
          existingItem.categories.push({
            id: row.category_id,
            name: row.category_name,
            description: row.category_description
          });
        }
      }
    });

    return Array.from(itemsMap.values());
  } catch (error) {
    console.error('Error in fetchMenuItemsFromView:', error);
    throw error;
  }
};

// API functions
const fetchMenuItemsFromEdgeFunction = async (storeId: string): Promise<MenuItem[]> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetchMenuItems`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        storeId,
        categoryId: 'all', // Always fetch all items for caching
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge function error:', response.status, errorText);
      throw new Error(`Edge function failed: ${response.status} ${errorText}`);
    }

    const result: EdgeFunctionResponse = await response.json();
    
    if (result.error) {
      throw new Error(result.error);
    }

    return result.data || [];
  } catch (error) {
    console.error('Error calling fetchMenuItems edge function:', error);
    throw error;
  }
};

const fetchCategoriesFromDbInternal = async (storeId: string): Promise<MenuCategory[]> => {
  try {
    const { data, error } = await supabase
      .from('menu_categories')
      .select('*')
      .eq('store_id', storeId)
      .order('position', { ascending: true });

    if (error) {
      console.error('Error fetching categories:', error);
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchCategoriesFromDb:', error);
    throw error;
  }
};

// Query keys
export const menuQueryKeys = {
  all: ['menu'] as const,
  items: (storeId: string) => [...menuQueryKeys.all, 'items', storeId] as const,
  categories: (storeId: string) => [...menuQueryKeys.all, 'categories', storeId] as const,
  filtered: (storeId: string, filters: MenuItemFilters) => 
    [...menuQueryKeys.items(storeId), 'filtered', filters] as const,
};

// Enhanced TanStack Query hooks with better caching
export const useMenuItems = (storeId: string | undefined) => {
  return useQuery({
    queryKey: menuQueryKeys.items(storeId!),
    queryFn: () => fetchMenuItemsFromView(storeId!, 'all'),
    enabled: !!storeId,
    staleTime: CACHE_CONFIG.MENU_ITEMS_STALE_TIME,
    gcTime: CACHE_CONFIG.MENU_ITEMS_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: CACHE_CONFIG.RETRY_ATTEMPTS,
    retryDelay: CACHE_CONFIG.RETRY_DELAY,
  });
};

export const useMenuCategories = (storeId: string | undefined) => {
  return useQuery({
    queryKey: menuQueryKeys.categories(storeId!),
    queryFn: () => fetchCategoriesFromDbInternal(storeId!),
    enabled: !!storeId,
    staleTime: CACHE_CONFIG.CATEGORIES_STALE_TIME,
    gcTime: CACHE_CONFIG.CATEGORIES_GC_TIME,
    refetchOnWindowFocus: false,
    retry: CACHE_CONFIG.RETRY_ATTEMPTS,
    retryDelay: CACHE_CONFIG.RETRY_DELAY,
  });
};

export const useFilteredMenuItems = (
  storeId: string | undefined,
  filters: MenuItemFilters
) => {
  const { data: allItems, isLoading, error } = useMenuItems(storeId);

  // Filter items based on the provided filters
  const filteredItems = React.useMemo(() => {
    if (!allItems) return [];

    let filtered = [...allItems];

    // Filter by category
    if (filters.categoryId && filters.categoryId !== 'all') {
      filtered = filtered.filter(item => 
        item.categoryIds?.includes(filters.categoryId!)
      );
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Filter by availability
    if (filters.availableOnly) {
      filtered = filtered.filter(item => item.available);
    }

    return filtered;
  }, [allItems, filters]);
  
  return {
    data: filteredItems,
    isLoading,
    error,
    allItems,
  };
};

/**
 * Enhanced cache management utilities for menu operations
 * 
 * This hook provides reusable methods for managing TanStack Query cache
 * with optimistic updates and comprehensive invalidation strategies.
 * 
 * @example
 * ```typescript
 * const { 
 *   invalidateMenuCache, 
 *   optimisticallyRemoveMenuItem, 
 *   rollbackMenuCache 
 * } = useMenuCacheUtils();
 * 
 * // Delete a menu item with optimistic updates
 * const handleDelete = async (itemId: string) => {
 *   try {
 *     optimisticallyRemoveMenuItem(storeId, itemId);
 *     await deleteMenuItemService(itemId, storeId);
 *     await invalidateMenuCache(storeId);
 *   } catch (error) {
 *     rollbackMenuCache(storeId);
 *   }
 * };
 * ```
 */
export const useMenuCacheUtils = () => {
  const queryClient = useQueryClient();

  const invalidateMenuItems = (storeId: string) => {
    queryClient.invalidateQueries({
      queryKey: menuQueryKeys.items(storeId),
    });
  };

  const invalidateCategories = (storeId: string) => {
    queryClient.invalidateQueries({
      queryKey: menuQueryKeys.categories(storeId),
    });
  };

  const invalidateAllMenuData = (storeId: string) => {
    queryClient.invalidateQueries({
      queryKey: menuQueryKeys.all,
    });
  };

  // Comprehensive cache invalidation for menu operations
  const invalidateMenuCache = async (storeId: string) => {
    if (!storeId) return;

    // Invalidate all menu-related queries
    invalidateMenuItems(storeId);
    invalidateCategories(storeId);
    invalidateAllMenuData(storeId);

    // Force immediate refetch of the queries
    await queryClient.refetchQueries({
      queryKey: ['menu', 'items', storeId],
    });
    await queryClient.refetchQueries({
      queryKey: ['menu', 'categories', storeId],
    });
  };

  // Optimistic update for menu item deletion
  const optimisticallyRemoveMenuItem = (storeId: string, itemId: string) => {
    if (!storeId) return;

    // Update main menu items query
    queryClient.setQueryData(
      menuQueryKeys.items(storeId),
      (oldData: MenuItem[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.filter(item => item.id !== itemId);
      }
    );

    // Update filtered queries
    queryClient.setQueriesData(
      { queryKey: menuQueryKeys.items(storeId) },
      (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object' || !('data' in oldData)) return oldData;
        const typedData = oldData as { data: MenuItem[] };
        return {
          ...typedData,
          data: typedData.data.filter((item: MenuItem) => item.id !== itemId)
        };
      }
    );
  };

  // Optimistic update for menu item creation
  const optimisticallyAddMenuItem = (storeId: string, newItem: MenuItem) => {
    if (!storeId) return;

    // Update main menu items query
    queryClient.setQueryData(
      menuQueryKeys.items(storeId),
      (oldData: MenuItem[] | undefined) => {
        if (!oldData) return [newItem];
        return [...oldData, newItem];
      }
    );

    // Update filtered queries
    queryClient.setQueriesData(
      { queryKey: menuQueryKeys.items(storeId) },
      (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object' || !('data' in oldData)) return oldData;
        const typedData = oldData as { data: MenuItem[] };
        return {
          ...typedData,
          data: [...typedData.data, newItem]
        };
      }
    );
  };

  // Optimistic update for menu item modification
  const optimisticallyUpdateMenuItem = (storeId: string, updatedItem: MenuItem) => {
    if (!storeId) return;

    // Update main menu items query
    queryClient.setQueryData(
      menuQueryKeys.items(storeId),
      (oldData: MenuItem[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(item => item.id === updatedItem.id ? updatedItem : item);
      }
    );

    // Update filtered queries
    queryClient.setQueriesData(
      { queryKey: menuQueryKeys.items(storeId) },
      (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object' || !('data' in oldData)) return oldData;
        const typedData = oldData as { data: MenuItem[] };
        return {
          ...typedData,
          data: typedData.data.map((item: MenuItem) => 
            item.id === updatedItem.id ? updatedItem : item
          )
        };
      }
    );
  };

  // Rollback optimistic updates on error
  const rollbackMenuCache = (storeId: string) => {
    if (!storeId) return;

    queryClient.invalidateQueries({
      queryKey: menuQueryKeys.items(storeId),
    });
    queryClient.invalidateQueries({
      queryKey: menuQueryKeys.categories(storeId),
    });
  };

  const prefetchMenuItems = async (storeId: string) => {
    await queryClient.prefetchQuery({
      queryKey: menuQueryKeys.items(storeId),
      queryFn: () => fetchMenuItemsFromView(storeId, 'all'),
      staleTime: CACHE_CONFIG.MENU_ITEMS_STALE_TIME,
    });
  };

  const prefetchCategories = async (storeId: string) => {
    await queryClient.prefetchQuery({
      queryKey: menuQueryKeys.categories(storeId),
      queryFn: () => fetchCategoriesFromDbInternal(storeId),
      staleTime: CACHE_CONFIG.CATEGORIES_STALE_TIME,
    });
  };

  const clearMenuCache = (storeId: string) => {
    queryClient.removeQueries({
      queryKey: menuQueryKeys.items(storeId),
    });
    queryClient.removeQueries({
      queryKey: menuQueryKeys.categories(storeId),
    });
  };

  return {
    invalidateMenuItems,
    invalidateCategories,
    invalidateAllMenuData,
    invalidateMenuCache,
    optimisticallyRemoveMenuItem,
    optimisticallyAddMenuItem,
    optimisticallyUpdateMenuItem,
    rollbackMenuCache,
    prefetchMenuItems,
    prefetchCategories,
    clearMenuCache,
  };
};

// Legacy functions for backward compatibility
export const fetchAllMenuItems = async (
  storeId: string
): Promise<MenuItem[]> => {
  try {
    return await fetchMenuItemsFromView(storeId, 'all');
  } catch (error) {
    console.error("Error fetching all menu items:", error);
    throw error;
  }
};

export const fetchMenuItems = async (
  categoryId?: string,
  storeId?: string
): Promise<MenuItem[]> => {
  if (!storeId) {
    console.error("Store ID is required for fetching menu items");
    return [];
  }

  try {
    // Use the view function directly with category filtering
    return await fetchMenuItemsFromView(storeId, categoryId);
  } catch (error) {
    console.error("Error in fetchMenuItems:", error);
    return [];
  }
};

export async function fetchCategoriesFromDb(
  storeId: string
): Promise<{ data: MenuCategory[] | null; error: string | null }> {
  try {
    const data = await fetchCategoriesFromDbInternal(storeId);
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
